import { computed, type ComputedRef, type Ref, ref } from 'vue';

import type { Orientation } from '../interfaces/orientation';
import type { ElementMargins, ElementRectangle, ElementOrigins } from '../interfaces/rect';
import type { UsePositionOptions } from '../interfaces/usePositionOptions';

const defaultOptions: UsePositionOptions = {
  targetMargin: 80,
  defaultYPosition: 'top',
  defaultXPosition: 'center',
  orientationClassPrefix: 'w-position--'
};

/**
 * Wyliczanie pozycji elementu (toolitp,dropdown) w zależności od wolnego miejsca
 *
 * @export
 * @param {Partial<UsePositionOptions>} [options]
 * @return {({
 *   target: Ref<Element | undefined>;
 *   position: Ref<string>;
 *   orientation: Ref<Orientation>;
 *   orientationClass: ComputedRef<{ [className: string]: boolean }>;
 *   setPosition: () => void;
 * })}
 */
export function usePosition(options?: Partial<UsePositionOptions>): {
  target: Ref<Element | undefined>;
  targetOrigin: Ref<ElementOrigins>;
  position: Ref<string>;
  horizontalPosition: Ref<string>;
  verticalPosition: Ref<string>;
  orientation: Ref<Orientation>;
  orientationClass: ComputedRef<{ [className: string]: boolean }>;
  setPosition: () => void;
} {
  const { targetMargin, defaultXPosition, defaultYPosition, orientationClassPrefix } = createOptions();

  /**
   * Element HTML, do którego liczona będzie pozycja
   */
  const target = ref<Element>();

  /**
   * Docelowy styl pozycji
   */
  const position = ref('');

  /**
   * Docelowy styl pozycji (Tylko oś X)
   */
  const horizontalPosition = ref('');

  /**
   * Docelowy styl pozycji (Tylko oś X)
   */
  const verticalPosition = ref('');

  /**
   * Wyliczona orientacja
   */
  const orientation = ref<Orientation>({ x: 'center', y: defaultYPosition });

  /**
   * Wstawia klasę odpowiedzialną za orientację dropdowna
   */
  const orientationClass = computed(() => ({
    [orientationClassPrefix + orientation.value.y]: true,
    [orientationClassPrefix + orientation.value.x]: true
  }));

  /**
   * Koordynaty środka targetu
   */
  const targetOrigin = computed(() => {
    const { targetOrigin } = getTargetValues();

    return targetOrigin;
  });

  /**
   * Pozycja X elementu względem całego dokumentu
   *
   * @param {ElementRectangle} targetRect
   * @param {ElementMargins} targetSpace
   * @param {('center' | 'left' | 'right')} position
   * @return {string}
   */
  function calculate3dTX(
    targetRect: ElementRectangle,
    targetSpace: ElementMargins,
    position: 'center' | 'left' | 'right'
  ): string {
    if (position === 'center') {
      return Math.round(targetSpace.left + window.scrollX + targetRect.width / 2) + 'px';
    }
    if (position === 'left') {
      return Math.round(targetSpace.left + window.scrollX) + 'px';
    }
    // right
    return Math.round(targetSpace.left + targetRect.width + window.scrollX) + 'px';
  }

  /**
   * Pozycja Y elementu względem całego dokumentu
   *
   * @param {ElementRectangle} targetRect
   * @param {ElementMargins} targetSpace
   * @param {('top' | 'bottom')} position
   * @return {string}
   */
  function calculate3dTY(
    targetRect: ElementRectangle,
    targetSpace: ElementMargins,
    position: 'top' | 'bottom'
  ): string {
    if (position === 'top') {
      return Math.round(targetSpace.top + window.scrollY) + 'px';
    }
    // bottom
    return Math.round(targetSpace.top + targetRect.height + window.scrollY) + 'px';
  }

  /**
   * Przesunięcie w osi X
   *
   * @param {('center' | 'left' | 'right')} position
   * @return {string}
   */
  function calculateTX(position: 'center' | 'left' | 'right'): string {
    if (position === 'center') {
      return '-50%';
    }
    if (position === 'left') {
      return '0';
    }
    // right
    return '-100%';
  }

  /**
   * Przesunięcie w osi Y
   *
   * @param {('top' | 'bottom')} position
   * @return {string}
   */
  function calcluateTY(position: 'top' | 'bottom'): string {
    if (position === 'top') {
      return '-100%';
    }
    // bottom
    return '0';
  }

  /**
   * Przypisuje pozycję targetu oraz jego wolną przestrzeń na viewporcie
   *
   * @return {{ targetRect: ElementRectangle; targetSpace: ElementMargins; targetOrigin: ElementOrigins; }}
   */
  function getTargetValues(): {
    targetRect: ElementRectangle;
    targetSpace: ElementMargins;
    targetOrigin: ElementOrigins;
  } {
    if (!target.value) {
      return {
        targetRect: { bottom: 0, height: 0, left: 0, right: 0, top: 0, width: 0, x: 0, y: 0 },
        targetSpace: { bottom: 0, left: 0, right: 0, top: 0 },
        targetOrigin: { x: 0, y: 0 }
      };
    }
    const targetRect: ElementRectangle = target.value?.getBoundingClientRect();
    const targetSpace: ElementMargins = {
      left: targetRect.left,
      right: window.innerWidth - targetRect.left - targetRect.width,
      top: targetRect.top,
      bottom: window.innerHeight - targetRect.top - targetRect.height
    };

    const targetOrigin: ElementOrigins = {
      x: targetRect.left + targetRect.width / 2,
      y: targetRect.top + targetRect.height / 2
    };

    return { targetRect, targetSpace, targetOrigin };
  }

  /**
   * Sprawdza jaką orientację powinien mieć element
   *
   * @param {ElementMargins} targetSpace
   * @return {Orientation}
   */
  function getOrientation(targetSpace: ElementMargins): Orientation {
    const orientation: Orientation = { y: defaultYPosition, x: defaultXPosition };

    if ((defaultYPosition === 'top' && targetSpace.top < targetMargin) || targetSpace.top < targetSpace.bottom) {
      orientation.y = 'bottom';
    } else if (defaultYPosition === 'bottom' && targetSpace.bottom < targetMargin) {
      orientation.y = 'top';
    }

    if (targetSpace.left < targetMargin) {
      orientation.x = 'left';
    } else if (targetSpace.right < targetMargin) {
      orientation.x = 'right';
    }

    return orientation;
  }

  /**
   * Wylicza pozycję
   *
   */
  function setPosition(): void {
    const { targetRect, targetSpace } = getTargetValues();
    const newOrientation = getOrientation(targetSpace);

    if (orientation.value.x !== newOrientation.x || orientation.value.y !== newOrientation.y) {
      orientation.value = newOrientation;
    }

    const translate3dTX = calculate3dTX(targetRect, targetSpace, orientation.value.x);
    const translate3dTY = calculate3dTY(targetRect, targetSpace, orientation.value.y);
    const translateTX = calculateTX(orientation.value.x);
    const translateTY = calcluateTY(orientation.value.y);

    const newPosition =
      'transform:' +
      'translate3d(' +
      translate3dTX +
      ',' +
      translate3dTY +
      ',' +
      '0)' +
      'translate(' +
      translateTX +
      ',' +
      translateTY +
      ')';

    const newVerticalPosition =
      'transform:' +
      'translate3d(' +
      '0' +
      ',' +
      translate3dTY +
      ',' +
      '0)' +
      'translate(' +
      '0' +
      ',' +
      translateTY +
      ')';

    const newHorizontalPosition =
      'transform:' +
      'translate3d(' +
      translate3dTX +
      ',' +
      '0' +
      ',' +
      '0)' +
      'translate(' +
      translateTX +
      ',' +
      '0' +
      ')';

    if (position.value !== newPosition) {
      position.value = newPosition;
    }

    if (verticalPosition.value !== newVerticalPosition) {
      verticalPosition.value = newVerticalPosition;
    }

    if (horizontalPosition.value !== newHorizontalPosition) {
      horizontalPosition.value = newHorizontalPosition;
    }
  }

  function createOptions(): UsePositionOptions {
    const newOptions = { ...defaultOptions, ...options };

    if (typeof newOptions.targetMargin === 'number') {
      return newOptions;
    }

    // FIXME: Możliwe, że docelowo trzeba zmienić bibliotekę na zewnętrzną (np. floating-ui)
    newOptions.targetMargin = parseInt(newOptions.targetMargin);
    return newOptions;
  }

  return {
    orientationClass,
    orientation,
    position,
    target,
    targetOrigin,
    horizontalPosition,
    verticalPosition,
    setPosition
  };
}
